/**
 * "Vínculo do carimbo do tempo ao registro de evidências" (E183.F2, GUS-157, ADR-0134): o VCT do
 * caso na tela de certificados. Por carimbo, o arquivo que o resumo carimbado cobre (ou os arquivos
 * testados, quando não cobre nenhum), o resultado consolidado e cada verificação de V1 a V12 com o
 * estado gravado, o motivo e os valores comparados. É aqui (e na trilha) que fica o detalhe por
 * verificação, e não no exame. A tela só mostra o que o servidor compôs.
 */
import {
  Badge,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  type BadgeProps,
} from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import type {
  CarimboDoCasoData,
  ConsolidadoDoCarimbo,
  EstadoVct,
  VerificacaoDoCarimboData,
  VinculoDoCarimboData,
} from '@/lib/certificados_e_carimbo';

const t = ptBR.certificadosECarimbo;

type Variante = NonNullable<BadgeProps['variant']>;

const VARIANTE_DO_ESTADO: Record<EstadoVct, Variante> = {
  confere: 'success',
  nao_confere: 'danger',
  alerta: 'warning',
  nao_verificado: 'neutral',
  nao_se_aplica: 'neutral',
};

const VARIANTE_DO_CONSOLIDADO: Record<ConsolidadoDoCarimbo, Variante> = {
  confere: 'success',
  nao_confere: 'danger',
  nao_vinculado: 'danger',
  alerta: 'warning',
  nao_verificado: 'neutral',
};

function preencher(texto: string, valores: Record<string, string>): string {
  return Object.entries(valores).reduce(
    (acumulado, [chave, valor]) => acumulado.replaceAll(`{${chave}}`, valor),
    texto,
  );
}

function Verificacao({ v }: { v: VerificacaoDoCarimboData }): JSX.Element {
  return (
    <TableRow data-testid="vinculo-verificacao" data-id={v.id} data-estado={v.estado}>
      <TableCell className="w-[30%] align-top">
        <span className="font-mono text-caption text-fg-muted">{v.id}</span>{' '}
        <span className="font-medium text-fg">{v.nome}</span>
      </TableCell>
      <TableCell className="w-[16%] align-top">
        <Badge variant={VARIANTE_DO_ESTADO[v.estado]} data-estado={v.estado}>
          {t.estadoVct[v.estado]}
        </Badge>
      </TableCell>
      <TableCell className="align-top">
        {v.motivo !== null ? <p className="text-body-sm text-fg">{v.motivo}</p> : null}
        {v.detalhes.length > 0 ? (
          <dl className="mt-1 grid grid-cols-[minmax(8rem,auto)_1fr] gap-x-sc-3 text-body-sm">
            {v.detalhes.map((d, i) => (
              <div key={`${d.rotulo}-${i}`} className="contents">
                <dt className="text-fg-muted">{d.rotulo}</dt>
                <dd className="break-words font-mono text-[13px] text-fg">{d.valor}</dd>
              </div>
            ))}
          </dl>
        ) : null}
      </TableCell>
    </TableRow>
  );
}

function Carimbo({ c }: { c: CarimboDoCasoData }): JSX.Element {
  return (
    <section className="border border-border bg-surface p-sc-4" data-testid="vinculo-carimbo">
      <header className="mb-sc-3 flex flex-wrap items-center gap-sc-2">
        <h3 className="text-heading-sm text-fg">
          {preencher(t.vinculo.carimbo, { carimbo: c.carimbo })}
        </h3>
        <Badge variant={VARIANTE_DO_CONSOLIDADO[c.consolidado]} data-consolidado={c.consolidado}>
          {`${t.vinculo.consolidado}: ${t.consolidado[c.consolidado]}`}
        </Badge>
      </header>
      <p className="text-body-sm text-fg" data-testid="vinculo-coberto">
        {c.coberto !== null
          ? preencher(t.vinculo.coberto, { arquivo: c.coberto })
          : t.vinculo.semPar}
      </p>
      {c.instante !== null ? (
        <p className="text-caption text-fg-muted">
          {preencher(t.vinculo.instante, { instante: c.instante })}
        </p>
      ) : null}
      {c.testados.length > 0 ? (
        <div className="mt-sc-2" data-testid="vinculo-testados">
          <p className="text-caption font-medium text-fg-muted">{t.vinculo.testados}</p>
          <ul className="list-disc pl-sc-4 text-body-sm text-fg">
            {c.testados.map((nome, i) => (
              <li key={`${nome}-${i}`}>{nome}</li>
            ))}
          </ul>
        </div>
      ) : null}
      <Table className="mt-sc-3">
        <TableHeader>
          <TableRow>
            <TableHead>{t.vinculo.colunas.verificacao}</TableHead>
            <TableHead>{t.vinculo.colunas.estado}</TableHead>
            <TableHead>{t.vinculo.colunas.registro}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {c.verificacoes.map((v) => (
            <Verificacao key={v.id} v={v} />
          ))}
        </TableBody>
      </Table>
    </section>
  );
}

export function VinculoDoCarimbo({ vinculo }: { vinculo: VinculoDoCarimboData }): JSX.Element {
  return (
    <section
      className="flex flex-col gap-sc-3"
      data-testid="vinculo-do-carimbo"
      data-situacao={vinculo.situacao}
    >
      <header>
        <h2 className="text-heading-sm text-fg">{t.vinculo.titulo}</h2>
        <p className="text-caption text-fg-muted">{t.vinculo.subtitulo}</p>
      </header>
      {vinculo.motivo !== null ? (
        <p className="text-body-sm text-fg-muted" data-testid="vinculo-motivo">
          {vinculo.motivo}
        </p>
      ) : null}
      {vinculo.carimbos.map((c) => (
        <Carimbo key={c.carimbo_sha256} c={c} />
      ))}
    </section>
  );
}
